import Layout from "@/components/elements/Layout";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import CountryCombobox from "@/components/form/CountryCombobox";
import useAuth from "@/hooks/auth";
import { useCountry } from "@/hooks/useCountry";
import { useRegion } from "@/hooks/useRegion";
import { useSector } from "@/hooks/useSector";
import { useStage } from "@/hooks/useStage";
import {
  getCuratedShortlistSession,
  submitCuratedShortlistIntake,
} from "@/services/api/CuratedShortlist";
import { getCountriesByRegionId } from "@/services/api/UnicornTracker";
import {
  CaretUpDown,
  Check,
  CheckCircle,
  Clock,
  EnvelopeSimple,
  Spinner,
  UploadSimple,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

const INVESTOR_TYPES = [
  { value: "vc", label: "Venture Capital (VC)" },
  { value: "angels", label: "Angel Investors" },
  { value: "fo", label: "Family Offices (FO)" },
  { value: "strategics", label: "Strategics / Corporate" },
  { value: "other", label: "Other" },
];

const CURRENCIES = ["USD", "AED", "EUR", "GBP", "SAR", "SGD", "INR", "Other"];

const initialForm = {
  founderName: "",
  email: "",
  companyName: "",
  companyWebsite: "",
  pitchDeck: null,
  pitchDeckConsent: false,
  stage: "",
  roundSize: "",
  currency: "USD",
  sectors: [],
  geographyIncorporation: "",
  operatingRegion: "",
  operatingCountries: [],
  revenueStatus: "",
  raisedBefore: "",
  raisedBeforeDetails: "",
  leadInvestor: "",
  investorTypesInclude: [],
  investorTypesExclude: [],
  exclusions: "",
  additionalNotes: "",
};

export default function CuratedShortlistSuccess() {
  const { user } = useAuth({
    middleware: "auth",
    redirectIfAuthenticated: false,
  });
  const router = useRouter();
  const { data: stages, isLoading: stagesLoading } = useStage({
    searchStartupDropdown: true,
  });
  const { data: sectors, isLoading: sectorsLoading } = useSector({
    searchStartupDropdown: true,
  });
  const { countries, isLoading: countriesLoading } = useCountry({
    searchStartupDropdown: true,
  });
  const { regions, isLoading: regionsLoading } = useRegion({
    searchStartupDropdown: true,
  });
  const [operatingCountriesLoading, setOperatingCountriesLoading] =
    useState(false);

  const handleOperatingRegionSelect = async (regionId) => {
    setForm((prev) => ({
      ...prev,
      operatingRegion: regionId,
      operatingCountries: [],
    }));
    if (!regionId) return;
    setOperatingCountriesLoading(true);
    try {
      const res = await getCountriesByRegionId(regionId);
      const fetched = (res.data || []).map((c) => ({
        id: c.id,
        country_name: c.country_name,
      }));
      setForm((prev) => ({ ...prev, operatingCountries: fetched }));
    } catch (e) {
      console.error("Error fetching region countries:", e);
    } finally {
      setOperatingCountriesLoading(false);
    }
  };

  const [form, setForm] = useState({ ...initialForm });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [estimatedDelivery, setEstimatedDelivery] = useState(null);
  const fileInputRef = useRef(null);

  // Payment verification state
  const [sessionId, setSessionId] = useState(null);
  const [isVerifying, setIsVerifying] = useState(true);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);

  // Pre-fill form when user data is available
  useEffect(() => {
    if (user) {
      setForm((prev) => ({
        ...prev,
        founderName: prev.founderName || user.name || "",
        email: prev.email || user.email || "",
        companyName: prev.companyName || user.startup?.name || "",
        companyWebsite: prev.companyWebsite || user.startup?.website || "",
      }));
    }
  }, [user]);

  // Verify payment on mount
  useEffect(() => {
    const session_id = router.query.session_id;

    if (!router.isReady) return;

    if (!session_id) {
      router.replace("/investors");
      return;
    }

    setSessionId(session_id);

    const verifyPayment = async () => {
      setIsVerifying(true);
      try {
        const { data } = await getCuratedShortlistSession(session_id);

        if (data.payment_status === "paid") {
          setPaymentVerified(true);
          if (data.intake_submitted) {
            setAlreadySubmitted(true);
          }
          setForm((prev) => ({
            ...prev,
            email: prev.email || data.email || "",
            companyName: prev.companyName || data.startup?.name || "",
            companyWebsite: prev.companyWebsite || data.startup?.website || "",
          }));
        } else {
          // Payment not yet confirmed — poll once after a short delay
          setTimeout(async () => {
            try {
              const { data: retryData } =
                await getCuratedShortlistSession(session_id);
              if (retryData.payment_status === "paid") {
                setPaymentVerified(true);
                if (retryData.intake_submitted) {
                  setAlreadySubmitted(true);
                }
                setForm((prev) => ({
                  ...prev,
                  email: prev.email || retryData.email || "",
                  companyName: prev.companyName || retryData.startup?.name || "",
                  companyWebsite: prev.companyWebsite || retryData.startup?.website || "",
                }));
              }
            } catch {
              // ignore retry errors
            } finally {
              setIsVerifying(false);
            }
          }, 3000);
          return; // don't set isVerifying false yet — wait for retry
        }
      } catch (error) {
        console.error("Error verifying payment:", error);
        toast.error("Unable to verify payment. Please try refreshing.");
      }
      setIsVerifying(false);
    };

    verifyPayment();
  }, [router.isReady, router.query.session_id, router]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const toggleArrayItem = (key, value) => {
    setForm((prev) => {
      const arr = prev[key];
      return {
        ...prev,
        [key]: arr.includes(value)
          ? arr.filter((v) => v !== value)
          : [...arr, value],
      };
    });
  };

  const validate = () => {
    const e = {};
    if (!form.founderName.trim()) e.founderName = "Founder name is required.";
    if (!form.email.trim()) e.email = "Email is required.";
    if (!form.companyName.trim()) e.companyName = "Company name is required.";
    if (!form.companyWebsite.trim())
      e.companyWebsite = "Company website is required.";
    if (!form.stage) e.stage = "Stage is required.";
    if (!form.roundSize) e.roundSize = "Target round size is required.";
    if (form.sectors.length === 0)
      e.sectors = "Please select at least one sector.";
    if (!form.geographyIncorporation.trim())
      e.geographyIncorporation = "Headquarter country is required.";
    if (!form.operatingRegion)
      e.operatingRegion = "Operating region is required.";
    if (form.operatingCountries.length === 0)
      e.operatingCountries = "Please select at least one operating country.";
    if (!form.revenueStatus) e.revenueStatus = "Revenue status is required.";
    if (!form.raisedBefore) e.raisedBefore = "This field is required.";
    if (!form.leadInvestor) e.leadInvestor = "This field is required.";
    if (form.pitchDeck && !form.pitchDeckConsent)
      e.pitchDeckConsent = "Please give consent to upload your pitch deck.";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const firstErrorKey = Object.keys(validationErrors)[0];
      document
        .getElementById(firstErrorKey)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setErrors({});
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("session_id", sessionId);
      formData.append("founder_name", form.founderName);
      formData.append("email", form.email);
      formData.append("company_name", form.companyName);
      formData.append("company_website", form.companyWebsite);
      formData.append("stage", form.stage);
      formData.append("target_round_size", form.roundSize);
      formData.append("target_round_currency", form.currency);
      form.sectors.forEach((s) => formData.append("sectors[]", s));
      formData.append("geography_incorporation", form.geographyIncorporation);
      const operatingRegionObj = regions.find(
        (r) => String(r.id) === form.operatingRegion,
      );
      if (operatingRegionObj) {
        formData.append("geography_operating_region", operatingRegionObj.name);
      }
      form.operatingCountries.forEach((c) =>
        formData.append("geography_operating_countries[]", c.country_name),
      );
      formData.append(
        "geography_operating",
        form.operatingCountries.map((c) => c.country_name).join(", "),
      );
      formData.append("revenue_status", form.revenueStatus);
      formData.append("raised_before", form.raisedBefore);
      formData.append("raised_before_details", form.raisedBeforeDetails);
      formData.append("lead_investor", form.leadInvestor);
      formData.append(
        "investor_type_preferences",
        JSON.stringify({
          include: form.investorTypesInclude,
          exclude: form.investorTypesExclude,
        }),
      );
      formData.append("exclusions", form.exclusions);
      formData.append("additional_notes", form.additionalNotes);

      if (form.pitchDeck) {
        formData.append("pitch_deck", form.pitchDeck);
        formData.append("deck_consent", "1");
      }

      const { data } = await submitCuratedShortlistIntake(formData);
      setEstimatedDelivery(data.estimated_delivery || null);
      setSubmitted(true);
      toast.success("Intake submitted successfully!");
    } catch (error) {
      const status = error?.response?.status;
      if (status === 403) {
        toast.error("Payment not verified. Please contact support.");
      } else if (status === 409) {
        setAlreadySubmitted(true);
        toast.error("Intake has already been submitted.");
      } else if (status === 422) {
        const serverErrors = error?.response?.data?.errors || {};
        setErrors(serverErrors);
        toast.error("Please fix the errors and try again.");
      } else {
        toast.error(
          error?.response?.data?.message ||
            "Failed to submit intake. Please try again.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Loading / verifying state ---
  if (isVerifying) {
    return (
      <Layout title='Verifying Payment...'>
        <div className='container max-w-2xl py-20 text-center'>
          <Spinner className='mx-auto mb-4 text-5xl text-white/40 animate-spin' />
          <h1 className='mb-2 text-2xl font-semibold tracking-tight font-display'>
            Confirming your payment...
          </h1>
          <p className='text-sm text-white/60'>
            This usually takes a few seconds. Please don&apos;t close this page.
          </p>
        </div>
      </Layout>
    );
  }

  // --- Payment not verified ---
  if (!paymentVerified) {
    return (
      <Layout title='Payment Not Verified'>
        <div className='container max-w-2xl py-20 text-center'>
          <div className='inline-flex items-center justify-center w-20 h-20 mb-6 rounded-full bg-yellow-500/10'>
            <WarningCircle weight='fill' className='text-5xl text-yellow-400' />
          </div>
          <h1 className='mb-3 text-2xl font-semibold tracking-tight font-display'>
            Payment not confirmed yet
          </h1>
          <p className='max-w-md mx-auto mb-6 text-sm text-white/60'>
            We couldn&apos;t verify your payment. This may take a moment — try
            refreshing. If the issue persists, please contact support.
          </p>
          <div className='flex items-center justify-center gap-3'>
            <Button
              onClick={() => window.location.reload()}
              className='bg-white text-background hover:bg-white/90'
            >
              Refresh
            </Button>
            <a
              href='mailto:partnerships@lucidityinsights.com'
              className='text-sm underline text-white/60 hover:text-white'
            >
              Contact support
            </a>
          </div>
        </div>
      </Layout>
    );
  }

  // --- Already submitted ---
  if (alreadySubmitted) {
    return (
      <Layout title='Intake Already Submitted'>
        <div className='container max-w-2xl py-20 text-center'>
          <div className='inline-flex items-center justify-center w-20 h-20 mb-6 rounded-full bg-green-500/10'>
            <CheckCircle weight='fill' className='text-5xl text-green-400' />
          </div>
          <h1 className='mb-3 text-3xl font-semibold tracking-tight font-display'>
            We have everything we need!
          </h1>
          <p className='max-w-md mx-auto mb-2 text-white/70'>
            Your intake form has already been submitted. Our team is working on
            your curated investor shortlist.
          </p>
          <p className='mb-8 text-sm text-white/40'>
            You&apos;ll receive your shortlist within{" "}
            <span className='font-semibold text-white/60'>
              10 business days
            </span>
            .
          </p>
          <p className='text-sm text-white/40'>
            Questions? Email us at{" "}
            <a
              href='mailto:partnerships@lucidityinsights.com'
              className='underline text-white/60 hover:text-white'
            >
              partnerships@lucidityinsights.com
            </a>
          </p>
        </div>
      </Layout>
    );
  }

  // --- Submitted just now ---
  if (submitted) {
    return (
      <Layout title='Intake Submitted — Curated Investor Shortlist'>
        <div className='container max-w-2xl py-20 text-center'>
          <div className='inline-flex items-center justify-center w-20 h-20 mb-6 rounded-full bg-green-500/10'>
            <CheckCircle weight='fill' className='text-5xl text-green-400' />
          </div>
          <h1 className='mb-3 text-3xl font-semibold tracking-tight font-display'>
            Intake Submitted!
          </h1>
          <p className='max-w-md mx-auto mb-2 text-white/70'>
            Thank you for submitting your request to receive your curated investor shortlist. Someone from our consulting team will reach out to you to better understand your startup and pitch deck so we can create the most effective report and list of target investors for you.
          </p>
          <p className='text-sm text-white/40'>
            Questions? Email us at{" "}
            <a
              href='mailto:partnerships@lucidityinsights.com'
              className='underline text-white/60 hover:text-white'
            >
              partnerships@lucidityinsights.com
            </a>
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title='Curated Investor Shortlist — Complete Your Intake'>
      <div className='container max-w-3xl py-10 lg:py-14'>
        {/* Page header */}
        <div className='mb-8'>
          <div className='inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/15 border border-green-500/30 text-green-400 text-xs font-semibold mb-4'>
            <Check weight='bold' />
            Payment received
          </div>
          <h1 className='mb-4 text-3xl font-semibold tracking-tight lg:text-4xl font-display'>
            Complete your intake to start your shortlist
          </h1>
          <p className='text-sm text-white/60'>
            Fill in the details below so we can find the best-fit investors for
            your startup.
          </p>
        </div>

        {/* Info bar */}
        <div className='flex flex-wrap gap-2 p-4 mb-8 text-sm border rounded-xl bg-white/5 border-white/10'>
          <div className='flex items-center gap-2 text-white/70'>
            <Clock className='text-base text-white/40' />
            <span>
              Delivery in{" "}
              <span className='font-semibold text-white'>10 business days</span>{" "}
              from the first call
            </span>
          </div>
          <div className='hidden w-px lg:block bg-white/10' />
          <div className='flex items-center gap-2 text-white/70'>
            <EnvelopeSimple className='text-base text-white/40' />
            <span>
              Support:{" "}
              <a
                href='mailto:partnerships@lucidityinsights.com'
                className='underline hover:text-white'
              >
                partnerships@lucidityinsights.com
              </a>
            </span>
          </div>
        </div>

        {/* Legal notice */}
        <ul className='pl-4 mb-8 space-y-1 text-sm list-disc opacity-50'>
          <li>This is a research and matching service, not brokerage.</li>
          <li>No outcome guarantees (responses, meetings, funding).</li>
          <li>Shortlist size varies depending on niche and market.</li>
          <li>We do not share your deck externally without consent.</li>
        </ul>

        {/* Intake form */}
        <form onSubmit={handleSubmit} className='space-y-8'>
          {/* — Basic info — */}
          <section>
            <h2 className='pb-2 mb-4 text-lg font-semibold border-b border-white/10'>
              Your Details
            </h2>
            <div className='grid grid-cols-1 gap-5 lg:grid-cols-2'>
              <FormField
                id='founderName'
                label='Founder Name'
                required
                error={errors.founderName || errors.founder_name}
              >
                <Input
                  id='founderName'
                  value={form.founderName}
                  onChange={(e) => set("founderName", e.target.value)}
                  placeholder='Your full name'
                  className='text-white bg-white/5 border-white/10 placeholder:text-white/30'
                />
              </FormField>

              <FormField id='email' label='Email' required error={errors.email}>
                <Input
                  id='email'
                  type='email'
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder='you@company.com'
                  className='text-white bg-white/5 border-white/10 placeholder:text-white/30'
                />
              </FormField>

              <FormField
                id='companyName'
                label='Company Name'
                required
                error={errors.companyName || errors.company_name}
              >
                <Input
                  id='companyName'
                  value={form.companyName}
                  onChange={(e) => set("companyName", e.target.value)}
                  placeholder='Acme Inc.'
                  className='text-white bg-white/5 border-white/10 placeholder:text-white/30'
                />
              </FormField>

              <FormField
                id='companyWebsite'
                label='Company Website'
                required
                error={errors.companyWebsite || errors.company_website}
              >
                <Input
                  id='companyWebsite'
                  value={form.companyWebsite}
                  onChange={(e) => set("companyWebsite", e.target.value)}
                  placeholder='https://yourcompany.com'
                  className='text-white bg-white/5 border-white/10 placeholder:text-white/30'
                />
              </FormField>
            </div>
          </section>

          {/* — Pitch deck — */}
          <section>
            <h2 className='pb-2 mb-4 text-lg font-semibold border-b border-white/10'>
              Pitch Deck{" "}
              <span className='text-sm font-normal text-white/40'>
                (Optional)
              </span>
            </h2>
            <div
              className='p-6 text-center transition-colors border-2 border-dashed cursor-pointer border-white/20 rounded-xl hover:border-white/40'
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadSimple className='mx-auto mb-2 text-3xl text-white/30' />
              {form.pitchDeck ? (
                <p className='text-sm font-medium text-white'>
                  {form.pitchDeck.name}
                </p>
              ) : (
                <p className='text-sm text-white/50'>
                  Click to upload your pitch deck (PDF, PPT, PPTX)
                </p>
              )}
              <input
                ref={fileInputRef}
                type='file'
                accept='.pdf,.ppt,.pptx'
                className='hidden'
                onChange={(e) => set("pitchDeck", e.target.files?.[0] || null)}
              />
            </div>
            {form.pitchDeck && (
              <div className='mt-3 flex items-start gap-2.5'>
                <Checkbox
                  id='pitchDeckConsent'
                  checked={form.pitchDeckConsent}
                  onCheckedChange={(v) => set("pitchDeckConsent", v)}
                  className='mt-0.5'
                />
                <Label
                  htmlFor='pitchDeckConsent'
                  className='text-sm leading-snug cursor-pointer text-white/70'
                >
                  I consent to Lucidity using this deck internally for matching.
                </Label>
              </div>
            )}
            {errors.pitchDeckConsent && (
              <p className='mt-1.5 text-xs text-red-400'>
                {errors.pitchDeckConsent}
              </p>
            )}
          </section>

          {/* — Funding details — */}
          <section>
            <h2 className='pb-2 mb-4 text-lg font-semibold border-b border-white/10'>
              Funding Details
            </h2>
            <div className='grid grid-cols-1 gap-5 lg:grid-cols-2'>
              <FormField
                id='stage'
                label='Funding Stage'
                required
                error={errors.stage}
              >
                <Select
                  value={form.stage}
                  onValueChange={(v) => set("stage", v)}
                >
                  <SelectTrigger
                    id='stage'
                    className='text-white bg-white/5 border-white/10'
                  >
                    <SelectValue placeholder='Select your stage' />
                  </SelectTrigger>
                  <SelectContent>
                    {stagesLoading ? (
                      <SelectItem value='__loading' disabled>
                        Loading...
                      </SelectItem>
                    ) : stages.length > 0 ? (
                      stages.map((s) => (
                        <SelectItem key={s.id} value={String(s.id)}>
                          {s.name}
                        </SelectItem>
                      ))
                    ) : (
                      // Fallback static list
                      [
                        "Angel",
                        "Pre-seed",
                        "Seed",
                        "Series A",
                        "Series B",
                        "Series B+",
                      ].map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </FormField>

              <FormField
                id='roundSize'
                label='Target Round Size'
                required
                error={errors.roundSize || errors.target_round_size}
              >
                <div className='flex gap-2'>
                  <Select
                    value={form.currency}
                    onValueChange={(v) => set("currency", v)}
                  >
                    <SelectTrigger className='flex-shrink-0 text-white bg-white/5 border-white/10 w-28'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    id='roundSize'
                    type='number'
                    min='0'
                    value={form.roundSize}
                    onChange={(e) => set("roundSize", e.target.value)}
                    placeholder='e.g. 1000000'
                    className='flex-1 text-white bg-white/5 border-white/10 placeholder:text-white/30'
                  />
                </div>
              </FormField>
            </div>

            <div className='mt-5'>
              <FormField
                id='sectors'
                label='Sector(s)'
                required
                error={errors.sectors}
              >
                <SectorCombobox
                  sectors={sectors}
                  loading={sectorsLoading}
                  selected={form.sectors}
                  onToggle={(id) => toggleArrayItem("sectors", id)}
                />
              </FormField>
            </div>
          </section>

          {/* — Geography — */}
          <section>
            <h2 className='pb-2 mb-4 text-lg font-semibold border-b border-white/10'>
              Geography Focus
            </h2>
            <div className='grid grid-cols-1 gap-5 lg:grid-cols-2'>
              <FormField
                id='geographyIncorporation'
                label='Headquarter Country'
                required
                className='lg:col-span-2'
                error={
                  errors.geographyIncorporation ||
                  errors.geography_incorporation
                }
              >
                <CountryCombobox
                  countries={countries}
                  loading={countriesLoading}
                  selected={form.geographyIncorporation}
                  onSelect={(name) => set("geographyIncorporation", name)}
                />
              </FormField>

              <FormField
                id='operatingRegion'
                label='Operating Region'
                required
                className='lg:col-span-2'
                error={errors.operatingRegion}
              >
                <RegionCombobox
                  regions={regions}
                  loading={regionsLoading}
                  selected={form.operatingRegion}
                  onSelect={handleOperatingRegionSelect}
                />
              </FormField>
              {/* Operating Countries — auto-filled from region, individually removable + searchable to re-add */}
              {(form.operatingRegion || form.operatingCountries.length > 0) && (
                <FormField
                  id='operatingCountries'
                  label='Operating Countries'
                  className='lg:col-span-2'
                  required
                  error={errors.operatingCountries}
                >
                  {operatingCountriesLoading ? (
                    <div className='flex items-center gap-2 px-3 py-2 text-sm border rounded-md border-white/10 bg-white/5 text-white/40'>
                      <Spinner className='animate-spin size-3.5' />
                      Loading countries...
                    </div>
                  ) : (
                    <div className='space-y-3'>
                      <AddCountryCombobox
                        countries={countries.filter(
                          (c) =>
                            !form.operatingCountries.some(
                              (oc) => oc.id === c.id,
                            ),
                        )}
                        loading={countriesLoading}
                        onAdd={(c) =>
                          setForm((prev) => ({
                            ...prev,
                            operatingCountries: [
                              ...prev.operatingCountries,
                              { id: c.id, country_name: c.country_name },
                            ],
                          }))
                        }
                      />
                      {form.operatingCountries.length > 0 ? (
                        <div className='flex flex-wrap gap-2'>
                          {form.operatingCountries.map((c) => (
                            <span
                              key={c.id}
                              className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 border border-white/15 text-xs text-white/80'
                            >
                              {c.country_name}
                              <button
                                type='button'
                                onClick={() =>
                                  setForm((prev) => ({
                                    ...prev,
                                    operatingCountries:
                                      prev.operatingCountries.filter(
                                        (x) => x.id !== c.id,
                                      ),
                                  }))
                                }
                                className='transition-colors hover:text-white'
                                aria-label={`Remove ${c.country_name}`}
                              >
                                <X className='text-[10px]' />
                              </button>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className='text-xs text-white/30'>
                          No countries selected. Search above to add one.
                        </p>
                      )}
                    </div>
                  )}
                </FormField>
              )}
            </div>
          </section>

          {/* — Fundraising context — */}
          <section>
            <h2 className='pb-2 mb-4 text-lg font-semibold border-b border-white/10'>
              Fundraising Context
            </h2>
            <div className='space-y-6'>
              <FormField
                id='revenueStatus'
                label='Revenue Status'
                required
                error={errors.revenueStatus || errors.revenue_status}
              >
                <RadioGroup
                  options={[
                    { value: "pre_revenue", label: "Pre-revenue" },
                    { value: "post_revenue", label: "Post-revenue" },
                  ]}
                  value={form.revenueStatus}
                  onChange={(v) => set("revenueStatus", v)}
                />
              </FormField>

              <FormField
                id='raisedBefore'
                label='Have you raised before?'
                required
                error={errors.raisedBefore || errors.raised_before}
              >
                <RadioGroup
                  options={[
                    { value: "yes", label: "Yes" },
                    { value: "no", label: "No" },
                  ]}
                  value={form.raisedBefore}
                  onChange={(v) => set("raisedBefore", v)}
                />
                {form.raisedBefore === "yes" && (
                  <div className='mt-3'>
                    <Input
                      value={form.raisedBeforeDetails}
                      onChange={(e) =>
                        set("raisedBeforeDetails", e.target.value)
                      }
                      placeholder='e.g. $500K pre-seed from angels in 2023'
                      className='text-white bg-white/5 border-white/10 placeholder:text-white/30'
                    />
                  </div>
                )}
              </FormField>

              <FormField
                id='leadInvestor'
                label='Are you looking for a lead investor?'
                required
                error={errors.leadInvestor || errors.lead_investor}
              >
                <RadioGroup
                  options={[
                    { value: "yes", label: "Yes" },
                    { value: "no", label: "No" },
                  ]}
                  value={form.leadInvestor}
                  onChange={(v) => set("leadInvestor", v)}
                />
              </FormField>
            </div>
          </section>

          {/* — Investor preferences — */}
          <section>
            <h2 className='pb-2 mb-4 text-lg font-semibold border-b border-white/10'>
              Investor Preferences{" "}
              <span className='text-sm font-normal text-white/40'>
                (Optional)
              </span>
            </h2>
            <div className='space-y-5'>
              <div>
                <p className='text-sm font-medium text-white/80 mb-2.5'>
                  Investor types to{" "}
                  <span className='text-green-400'>include</span>
                </p>
                <div className='flex flex-wrap gap-3'>
                  {INVESTOR_TYPES.map((t) => (
                    <label
                      key={t.value}
                      className='flex items-center gap-2 cursor-pointer'
                    >
                      <Checkbox
                        checked={form.investorTypesInclude.includes(t.value)}
                        onCheckedChange={() =>
                          toggleArrayItem("investorTypesInclude", t.value)
                        }
                      />
                      <span className='text-sm text-white/70'>{t.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <p className='text-sm font-medium text-white/80 mb-2.5'>
                  Investor types to{" "}
                  <span className='text-red-400'>exclude</span>
                </p>
                <div className='flex flex-wrap gap-3'>
                  {INVESTOR_TYPES.map((t) => (
                    <label
                      key={t.value}
                      className='flex items-center gap-2 cursor-pointer'
                    >
                      <Checkbox
                        checked={form.investorTypesExclude.includes(t.value)}
                        onCheckedChange={() =>
                          toggleArrayItem("investorTypesExclude", t.value)
                        }
                      />
                      <span className='text-sm text-white/70'>{t.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <FormField id='exclusions' label='Specific Exclusions'>
                <Textarea
                  id='exclusions'
                  value={form.exclusions}
                  onChange={(e) => set("exclusions", e.target.value)}
                  placeholder='e.g. No investors from specific regions, no competitors in portfolio...'
                  rows={3}
                  className='text-white resize-none bg-white/5 border-white/10 placeholder:text-white/30'
                />
              </FormField>

              <FormField id='additionalNotes' label='Additional Notes'>
                <Textarea
                  id='additionalNotes'
                  value={form.additionalNotes}
                  onChange={(e) => set("additionalNotes", e.target.value)}
                  placeholder="Anything else you'd like us to know..."
                  rows={3}
                  className='text-white resize-none bg-white/5 border-white/10 placeholder:text-white/30'
                />
              </FormField>
            </div>
          </section>

          {/* Submit */}
          <div className='pt-2 pb-8'>
            {Object.keys(errors).length > 0 && (
              <p className='mb-4 text-sm text-red-400'>
                Please fix the errors above before submitting.
              </p>
            )}
            <Button
              type='submit'
              disabled={isSubmitting}
              className='w-full h-auto py-3.5 text-base font-semibold bg-gradient-to-r from-purple-1 to-red-1 text-white hover:opacity-90'
            >
              {isSubmitting ? (
                <span className='flex items-center gap-2'>
                  <Spinner className='text-lg animate-spin' />
                  Submitting...
                </span>
              ) : (
                "Submit Intake"
              )}
            </Button>
            <p className='mt-3 text-xs text-center text-white/40'>
              Shortlist size varies by niche and market.
            </p>
          </div>
        </form>
      </div>
    </Layout>
  );
}

/* ── Helpers ────────────────────────────────────────── */

function AddCountryCombobox({ countries, loading, onAdd }) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          className='flex items-center justify-between w-full px-3 py-2 text-sm transition-colors border rounded-md border-white/10 bg-white/5 text-white/40 hover:bg-white/10'
        >
          Search and add a country...
          <CaretUpDown className='flex-shrink-0 ml-2 text-white/40' />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className='w-[--radix-popover-trigger-width] p-0'
        align='start'
      >
        <Command>
          <CommandInput placeholder='Search country...' />
          <CommandList>
            {loading ? (
              <CommandEmpty>Loading...</CommandEmpty>
            ) : (
              <>
                <CommandEmpty>No country found.</CommandEmpty>
                <CommandGroup>
                  {countries.map((c) => (
                    <CommandItem
                      key={c.id}
                      value={c.country_name}
                      onSelect={() => {
                        onAdd(c);
                        setOpen(false);
                      }}
                    >
                      {c.country_name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function RegionCombobox({ regions, loading, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const selectedRegion = regions.find((r) => String(r.id) === selected);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          className='flex items-center justify-between w-full px-3 py-2 text-sm transition-colors border rounded-md border-white/10 bg-white/5 text-white/60 hover:bg-white/10'
        >
          <span className={selectedRegion ? "text-white" : ""}>
            {selectedRegion
              ? selectedRegion.name
              : "Search and select region..."}
          </span>
          <CaretUpDown className='flex-shrink-0 ml-2 text-white/40' />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className='w-[--radix-popover-trigger-width] p-0'
        align='start'
      >
        <Command>
          <CommandInput placeholder='Search region...' />
          <CommandList>
            {loading ? (
              <CommandEmpty>Loading...</CommandEmpty>
            ) : (
              <>
                <CommandEmpty>No region found.</CommandEmpty>
                <CommandGroup>
                  {regions.map((r) => (
                    <CommandItem
                      key={r.id}
                      value={r.name}
                      onSelect={() => {
                        onSelect(String(r.id));
                        setOpen(false);
                      }}
                    >
                      <span className='flex-1'>{r.name}</span>
                      {selected === String(r.id) && (
                        <Check className='text-sm text-primary' />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function SectorCombobox({ sectors, loading, selected, onToggle }) {
  const [open, setOpen] = useState(false);

  const selectedSectors = sectors.filter((s) =>
    selected.includes(String(s.id)),
  );

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type='button'
            className='flex items-center justify-between w-full px-3 py-2 text-sm transition-colors border rounded-md border-white/10 bg-white/5 text-white/60 hover:bg-white/10'
          >
            {selected.length > 0
              ? `${selected.length} sector${selected.length > 1 ? "s" : ""} selected`
              : "Search and select sectors..."}
            <CaretUpDown className='flex-shrink-0 ml-2 text-white/40' />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className='w-[--radix-popover-trigger-width] p-0'
          align='start'
        >
          <Command>
            <CommandInput placeholder='Search sectors...' />
            <CommandList>
              {loading ? (
                <CommandEmpty>Loading...</CommandEmpty>
              ) : (
                <>
                  <CommandEmpty>No sector found.</CommandEmpty>
                  <CommandGroup>
                    {sectors.map((s) => {
                      const isSelected = selected.includes(String(s.id));
                      return (
                        <CommandItem
                          key={s.id}
                          value={s.name}
                          onSelect={() => onToggle(String(s.id))}
                        >
                          <span className='flex-1'>{s.name}</span>
                          {isSelected && (
                            <Check className='text-sm text-primary' />
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedSectors.length > 0 && (
        <div className='flex flex-wrap gap-2 mt-3'>
          {selectedSectors.map((s) => (
            <span
              key={s.id}
              className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 border border-white/15 text-xs text-white/80'
            >
              {s.name}
              <button
                type='button'
                onClick={() => onToggle(String(s.id))}
                className='transition-colors hover:text-white'
                aria-label={`Remove ${s.name}`}
              >
                <X className='text-[10px]' />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function FormField({ id, label, required, error, children, className }) {
  return (
    <div className={className}>
      <Label htmlFor={id} className='text-sm text-white/80 mb-1.5 block'>
        {label}
        {required && <span className='text-red-400 ml-0.5'>*</span>}
      </Label>
      {children}
      {error && <p className='mt-1 text-xs text-red-400'>{error}</p>}
    </div>
  );
}

function RadioGroup({ options, value, onChange }) {
  return (
    <div className='flex gap-4'>
      {options.map((opt) => (
        <label
          key={opt.value}
          className='flex items-center gap-2 cursor-pointer'
        >
          <input
            type='radio'
            className='w-4 h-4 accent-purple-500'
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
          />
          <span className='text-sm text-white/70'>{opt.label}</span>
        </label>
      ))}
    </div>
  );
}
